import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.turevisionsegura.com';

export default function Contacto() {
  const [formData, setFormData] = useState({
    full_name: '', phone: '', vehicle_type: '',
    license_plate: '', ally_code: '', how_did_you_hear: ''
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [acceptDataPolicy, setAcceptDataPolicy] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);

  useEffect(() => {
    document.title = 'Contacto REVISION SEGURA | Regístrate y Activa tus Beneficios · Bogotá Colombia';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Regístrate en la Plataforma de Gestión de Beneficios de la Alianza Técnica. Al registrar tus datos en nuestro taller aliado, activas automáticamente tu Kit de Protección y tu cobertura de asistencias.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/contacto`);
    return () => {};
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const showNotification = (type: 'success' | 'error' | 'warning', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 6000);
  };

  const validateLicensePlate = (plate: string, vehicleType: string): boolean => {
    const p = plate.trim().toUpperCase();
    if (vehicleType === 'moto') {
      return /^[A-Z]{3}[0-9]{2}[A-Z]$/.test(p) || /^[A-Z]{3}[0-9]{2}$/.test(p);
    }
    return /^[A-Z]{3}[0-9]{3}$/.test(p);
  };

  const getPlaceholderPlate = () => formData.vehicle_type === 'moto' ? 'ABC23D o ABC23' : 'ABC123';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!acceptDataPolicy) { showNotification('warning', 'Debes aceptar la política de uso de datos para continuar.'); return; }
    if (!formData.full_name.trim()) { showNotification('warning', 'Por favor ingresa tu nombre completo.'); return; }
    if (!formData.phone.trim()) { showNotification('warning', 'Por favor ingresa tu número de teléfono.'); return; }
    if (!formData.vehicle_type) { showNotification('warning', 'Por favor selecciona el tipo de vehículo.'); return; }
    if (!formData.license_plate.trim()) { showNotification('warning', 'Por favor ingresa la placa del vehículo.'); return; }
    if (!validateLicensePlate(formData.license_plate, formData.vehicle_type)) {
      if (formData.vehicle_type === 'moto') {
        showNotification('warning', 'Formato de placa inválido para moto. Debe ser ABC23D (con letra final) o ABC23 (sin letra final). Ejemplo: ABC23D o ABC23');
      } else {
        showNotification('warning', 'Formato de placa inválido. Debe ser 3 letras seguidas de 3 números. Ejemplo: ABC123');
      }
      return;
    }
    if (!formData.ally_code.trim()) { showNotification('warning', 'El código del taller aliado es obligatorio. Este código es entregado en el punto de atención presencial del taller u operador aliado.'); return; }
    if (!formData.how_did_you_hear) { showNotification('warning', 'Por favor indícanos cómo llegaste al programa.'); return; }

    setIsSubmitting(true);
    const submitData = {
      full_name: formData.full_name.trim(), phone: formData.phone.trim(),
      ally_code: formData.ally_code.trim(), vehicle_type: formData.vehicle_type.trim(),
      license_plate: formData.license_plate.trim().toUpperCase(), how_did_you_hear: formData.how_did_you_hear.trim()
    };
    try {
      const response = await fetch('https://lsddbkicdbwbilyhzcrf.supabase.co/functions/v1/submit-investment-registration', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(submitData)
      });
      const result = await response.json();
      if (result.success) {
        showNotification('success', '¡Registro completado! Tu información ha sido recibida. Presenta este registro en el taller aliado para activar tus beneficios y completar el proceso presencial.');
        setFormData({ full_name: '', phone: '', vehicle_type: '', license_plate: '', ally_code: '', how_did_you_hear: '' });
        setAcceptDataPolicy(false);
      } else {
        showNotification('error', result.error || 'Error al guardar el registro. Intenta nuevamente.');
      }
    } catch {
      showNotification('error', 'Error de conexión. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAgent = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  const glassCard = {
    background: 'rgba(10,22,40,0.65)',
    border: '1px solid rgba(32,192,208,0.18)',
    borderRadius: '4px',
    backdropFilter: 'blur(14px)',
  };

  const ctaBtn = {
    background: 'linear-gradient(135deg, #C87941, #E8A020)',
    borderRadius: '4px',
    boxShadow: '0 4px 20px rgba(200,121,65,0.35)',
  };

  const notifStyles = {
    success: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', icon: 'ri-check-circle-line text-emerald-400', text: 'text-emerald-400' },
    error: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)', icon: 'ri-error-warning-line text-red-400', text: 'text-red-400' },
    warning: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', icon: 'ri-alert-line text-amber-400', text: 'text-amber-400' },
  };

  return (
    <div className="min-h-screen font-sans overflow-x-hidden"
      style={{ background: 'radial-gradient(ellipse at 50% 30%, #0A1628 0%, #050810 55%, #000000 100%)' }}>

      {/* ── NOTIFICACIÓN FLOTANTE ── */}
      {notification && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 backdrop-blur-xl shadow-2xl transition-all duration-300 max-w-lg w-[90%]"
          style={{ background: notifStyles[notification.type].bg, border: `1px solid ${notifStyles[notification.type].border}`, borderRadius: '4px' }}>
          <i className={`${notifStyles[notification.type].icon} text-xl flex-shrink-0`}></i>
          <p className={`text-sm font-semibold ${notifStyles[notification.type].text}`}>{notification.message}</p>
          <button onClick={() => setNotification(null)} className="ml-auto text-white/30 hover:text-white/60 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      )}

      {/* ── NAV ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'backdrop-blur-xl shadow-2xl' : 'bg-transparent'}`}
        style={isScrolled ? { background: 'rgba(0,0,0,0.85)', borderBottom: '1px solid rgba(200,121,65,0.15)' } : {}}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/257ded5cf4b1165b373a01f4236f0b3a/e052755f997086e5f1ed7f5d0f41046d.png"
              alt="REVISION SEGURA Logo"
              className="h-10 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.9)) drop-shadow(0 0 8px rgba(200,121,65,0.3))' }}
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {([['/', 'Inicio'], ['/servicios', 'Recompensas'], ['/contacto', 'Contacto']] as [string, string][]).map(([to, label]) => (
              <Link key={to} to={to}
                className="text-xs font-semibold tracking-wider transition-colors whitespace-nowrap"
                style={{ color: to === '/contacto' ? '#C87941' : 'rgba(255,255,255,0.5)' }}>
                {label}
              </Link>
            ))}
          </div>
          <button className="md:hidden text-white/70 w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 px-6 py-4 flex flex-col gap-3"
            style={{ background: 'rgba(0,0,0,0.95)' }}>
            {([['/', 'Inicio'], ['/servicios', 'Recompensas'], ['/contacto', 'Contacto']] as [string, string][]).map(([to, label]) => (
              <Link key={to} to={to} className="text-sm text-white/70 hover:text-white font-medium" onClick={() => setIsMobileMenuOpen(false)}>{label}</Link>
            ))}
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ba72b918e7f25f697c1809af2cae3a93.jpg"
            alt="Contacto REVISION SEGURA"
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.3) saturate(0.6) hue-rotate(200deg)' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(5,8,16,0.5) 50%, #000000 100%)' }}></div>
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(16)].map((_, i) => (
            <div key={i} className="absolute w-1 h-1 rounded-full animate-pulse"
              style={{
                background: i % 2 === 0 ? '#C87941' : '#20C0D0',
                left: `${6 + i * 5.8}%`, top: `${15 + (i % 4) * 20}%`,
                animationDelay: `${i * 0.35}s`, opacity: 0.1 + (i % 3) * 0.07
              }} />
          ))}
        </div>

        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse"
          style={{ background: 'rgba(200,121,65,0.06)' }}></div>

        <div className="relative z-10 w-full max-w-3xl mx-auto px-6 text-center pt-24 pb-16">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2 mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#C87941' }}></span>
            <span className="text-xs text-white/60 font-bold uppercase tracking-widest">Alianza Técnica · Registro Oficial</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-white leading-none mb-6 tracking-tight">
            CONTÁCTANOS
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto leading-relaxed">
            Regístrate con el código de tu taller aliado en el Programa Empresarial Privado. Paga tu tarifa legal de <strong className="text-white/80">$370.000</strong> y activa de inmediato tu paquete de beneficios valorado en más de $300.000: Kit Profesional VELKO, Tarjeta Card Plus y Bono de Reembolso por Fidelidad si adquieres llantas con la alianza.
          </p>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-white/20 text-2xl"></i>
        </div>
      </section>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <section className="py-20" style={{ background: '#000000' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-10 items-start">

            {/* ── FORMULARIO ── */}
            <div className="lg:col-span-2">
              <div className="p-8 md:p-10 shadow-2xl" style={glassCard}>

                {/* Encabezado del formulario */}
                <div className="mb-8 pb-6" style={{ borderBottom: '1px solid rgba(200,121,65,0.15)' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #C87941, #E8A020)', borderRadius: '4px' }}>
                      <i className="ri-user-add-line text-white text-sm"></i>
                    </div>
                    <h2 className="text-white font-black text-xl tracking-tight">Formulario de Pre-Registro</h2>
                  </div>
                  <p className="text-white/45 text-sm leading-relaxed">
                    Completa tus datos con el <strong className="text-white/70">código entregado por el taller aliado</strong> o el promotor del Programa Empresarial Privado. El registro se formaliza de manera <strong style={{ color: '#C87941' }}>presencial en el punto de atención</strong> — este formulario es el paso previo para agilizar tu proceso. La coordinación de marcas y precios de llantas se realiza directamente con el Taller Aliado.
                  </p>
                  <div className="flex items-center gap-2 mt-3 px-3 py-2"
                    style={{ background: 'rgba(255,107,26,0.06)', border: '1px solid rgba(255,107,26,0.2)', borderRadius: '4px' }}>
                    <i className="ri-map-pin-2-line text-xs flex-shrink-0" style={{ color: '#FF6B1A' }}></i>
                    <p className="text-xs" style={{ color: 'rgba(255,107,26,0.75)' }}>
                      <strong>Canal presencial obligatorio:</strong> El proceso de activación de beneficios de la Alianza de Beneficios se completa únicamente en el taller aliado registrado.
                    </p>
                  </div>
                </div>

                <form id="contacto-cav-o2-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Nombre completo *</label>
                      <input type="text" id="full_name" name="full_name" value={formData.full_name} onChange={handleInputChange} required minLength={2}
                        className="w-full px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none transition-all"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(32,192,208,0.15)', borderRadius: '4px' }}
                        placeholder="Nombre y apellidos" />
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Celular de contacto *</label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required minLength={7} maxLength={15}
                        className="w-full px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none transition-all"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(32,192,208,0.15)', borderRadius: '4px' }}
                        placeholder="3001234567" />
                      <p className="mt-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
                        <i className="ri-information-line mr-1"></i>Número donde el taller aliado pueda verificar tu registro.
                      </p>
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Tipo de vehículo *</label>
                      <select id="vehicle_type" name="vehicle_type" value={formData.vehicle_type} onChange={handleInputChange} required
                        className="w-full px-4 py-3 text-white text-sm focus:outline-none transition-all cursor-pointer"
                        style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(32,192,208,0.15)', borderRadius: '4px' }}>
                        <option value="" className="bg-[#0A1628]">Selecciona el tipo</option>
                        <option value="particular" className="bg-[#0A1628]">Vehículo Particular</option>
                        <option value="publico_pasajeros" className="bg-[#0A1628]">Vehículo Público de Pasajeros</option>
                        <option value="publico_carga" className="bg-[#0A1628]">Vehículo Público de Carga</option>
                        <option value="moto" className="bg-[#0A1628]">MOTO</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Placa del vehículo *</label>
                      <input type="text" id="license_plate" name="license_plate" value={formData.license_plate} onChange={handleInputChange} required minLength={5} maxLength={7}
                        style={{ textTransform: 'uppercase', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(32,192,208,0.15)', borderRadius: '4px' }}
                        className="w-full px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none transition-all uppercase"
                        placeholder={getPlaceholderPlate()} />
                      {formData.vehicle_type === 'moto' && (
                        <p className="mt-1.5 text-xs" style={{ color: 'rgba(32,192,208,0.6)' }}>
                          <i className="ri-information-line mr-1"></i>Formato moto: <strong>ABC23D</strong> (con letra) o <strong>ABC23</strong> (sin letra)
                        </p>
                      )}
                      {formData.vehicle_type && formData.vehicle_type !== 'moto' && (
                        <p className="mt-1.5 text-xs" style={{ color: 'rgba(32,192,208,0.6)' }}>
                          <i className="ri-information-line mr-1"></i>Formato: <strong>ABC123</strong> (3 letras + 3 números)
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Código del taller aliado *</label>
                      <input type="text" id="ally_code" name="ally_code" value={formData.ally_code} onChange={handleInputChange} required maxLength={10}
                        style={{ textTransform: 'uppercase', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(32,192,208,0.15)', borderRadius: '4px' }}
                        className="w-full px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none transition-all uppercase"
                        placeholder="Ej: TRS-001" />
                      <p className="mt-1.5 text-xs" style={{ color: 'rgba(200,121,65,0.7)' }}>
                        <i className="ri-map-pin-line mr-1"></i>Código entregado en el punto de atención presencial del taller u operador aliado.
                      </p>
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">¿Cómo llegaste al programa? *</label>
                      <select id="how_did_you_hear" name="how_did_you_hear" value={formData.how_did_you_hear} onChange={handleInputChange} required
                        className="w-full px-4 py-3 text-white text-sm focus:outline-none transition-all cursor-pointer"
                        style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(32,192,208,0.15)', borderRadius: '4px' }}>
                        <option value="" className="bg-[#0A1628]">Selecciona una opción</option>
                        <option value="PROMOTOR EN EL TALLER" className="bg-[#0A1628]">PROMOTOR EN EL TALLER</option>
                        <option value="PROMOTOR EN CALLE" className="bg-[#0A1628]">PROMOTOR EN CALLE</option>
                        <option value="RECOMENDADO POR UN CONOCIDO" className="bg-[#0A1628]">RECOMENDADO POR UN CONOCIDO</option>
                        <option value="VOLANTE O MATERIAL IMPRESO" className="bg-[#0A1628]">VOLANTE O MATERIAL IMPRESO</option>
                        <option value="ENLACE WHATSAPP DEL TALLER" className="bg-[#0A1628]">ENLACE WHATSAPP DEL TALLER</option>
                        <option value="ALIADO VERIFICADO" className="bg-[#0A1628]">ALIADO VERIFICADO</option>
                      </select>
                    </div>
                  </div>

                  <div className="p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px' }}>
                    {/* Bloque Habeas Data */}
                    <div className="p-4" style={{ background: 'rgba(32,192,208,0.04)', border: '1px solid rgba(32,192,208,0.12)', borderRadius: '4px' }}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(32,192,208,0.12)', borderRadius: '4px' }}>
                          <i className="ri-shield-user-line text-xs" style={{ color: '#20C0D0' }}></i>
                        </div>
                        <span className="text-xs font-black uppercase tracking-widest" style={{ color: '#20C0D0' }}>Protección de Datos Personales — Ley 1581 de 2012</span>
                      </div>
                      <p className="text-white/35 text-xs leading-relaxed mb-3">
                        De conformidad con la <strong className="text-white/55">Ley Estatutaria 1581 de 2012</strong> y el <strong className="text-white/55">Decreto 1377 de 2013</strong> (Habeas Data), <strong className="text-white/55">Gestión Automotriz AW2020 SAS</strong> — NIT 901401495 — actúa como responsable del tratamiento de los datos personales suministrados en este formulario.
                      </p>
                      <ul className="space-y-1.5 mb-3">
                        {[
                          { icon: 'ri-focus-3-line', text: 'Finalidad: gestión del programa REVISION SEGURA, contacto comercial y envío de información relacionada.' },
                          { icon: 'ri-lock-line', text: 'Sus datos no serán cedidos ni vendidos a terceros sin su consentimiento previo.' },
                          { icon: 'ri-user-settings-line', text: 'Derechos: conocer, actualizar, rectificar y suprimir sus datos en cualquier momento.' },
                          { icon: 'ri-mail-send-line', text: 'Para ejercer sus derechos, escríbanos indicando su solicitud.' },
                        ].map((item) => (
                          <li key={item.icon} className="flex items-start gap-2 text-white/30 text-xs">
                            <i className={`${item.icon} flex-shrink-0 mt-0.5`} style={{ color: 'rgba(32,192,208,0.5)' }}></i>
                            <span>{item.text}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-white/20 text-[11px] leading-relaxed">
                        Al marcar la casilla de aceptación, usted otorga su consentimiento libre, previo, expreso e informado para el tratamiento de sus datos personales conforme a lo descrito.
                      </p>
                    </div>

                    {/* Checkbox aceptación */}
                    <div className="flex items-start gap-3">
                      <input type="checkbox" id="acceptDataPolicy" checked={acceptDataPolicy}
                        onChange={(e) => setAcceptDataPolicy(e.target.checked)}
                        className="mt-0.5 w-5 h-5 cursor-pointer flex-shrink-0" required
                        style={{ accentColor: '#C87941' }} />
                      <label htmlFor="acceptDataPolicy" className="text-sm text-white/50 cursor-pointer leading-relaxed">
                        <strong className="text-white font-bold">Acepto la política de tratamiento de datos personales</strong> y autorizo a Gestión Automotriz AW2020 SAS para tratar mi información conforme a la Ley 1581 de 2012.
                      </label>
                    </div>
                  </div>

                  <button type="submit" disabled={isSubmitting || !acceptDataPolicy}
                    className="w-full py-4 font-black text-sm tracking-widest uppercase transition-all duration-300 hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 whitespace-nowrap text-white"
                    style={ctaBtn}
                    onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.boxShadow = '0 0 28px 6px rgba(220,100,20,0.55)'; }}
                    onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(200,121,65,0.35)')}>
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-map-pin-2-line text-base"></i>
                        ENVIAR PRE-REGISTRO AL TALLER ALIADO
                      </span>
                    )}
                  </button>

                  <p className="text-xs text-white/25 text-center leading-relaxed">
                    Este pre-registro es el paso previo. La activación de beneficios se completa <strong className="text-white/40">presencialmente en el taller aliado</strong> al realizar tu RTM. El código del taller es indispensable para validar tu participación en el programa.
                  </p>
                </form>
              </div>
            </div>

            {/* ── SIDEBAR ── */}
            <div className="space-y-5">
              {/* Agente */}
              <div className="relative overflow-hidden p-8 text-center"
                style={{ ...glassCard, border: '1px solid rgba(200,121,65,0.25)' }}>
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(200,121,65,0.08) 0%, transparent 70%)' }}></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-xl"
                    style={{ background: 'linear-gradient(135deg, #C87941, #E8A020)', borderRadius: '4px', boxShadow: '0 0 30px rgba(200,121,65,0.35)' }}>
                    <i className="ri-robot-2-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-black text-white mb-2">¿Tienes dudas?</h3>
                  <p className="text-white/40 text-xs mb-5 leading-relaxed">
                    Habla con nuestro asistente virtual y obtén respuestas inmediatas sobre el incentivo de referencia, el registro y tus bonificaciones
                  </p>
                  <button onClick={openAgent}
                    className="w-full font-black px-5 py-3.5 text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap uppercase tracking-widest text-white"
                    style={ctaBtn}
                    onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 0 28px 6px rgba(220,100,20,0.55)')}
                    onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(200,121,65,0.35)')}>
                    HABLAR CON EL AGENTE
                  </button>
                </div>
              </div>

              {/* ── ESPACIO PARA IMAGEN ── */}
              <div className="relative overflow-hidden"
                style={{ ...glassCard, border: '1px solid rgba(200,121,65,0.3)' }}>
                {/* Contenedor imagen */}
                <div className="w-full flex flex-col items-center justify-center gap-3 py-10 px-6"
                  style={{ minHeight: '200px', background: 'linear-gradient(135deg, rgba(15,32,64,0.97) 0%, rgba(26,58,110,0.92) 100%)' }}>
                  <div className="w-14 h-14 flex items-center justify-center"
                    style={{ background: 'rgba(200,121,65,0.12)', border: '1px solid rgba(200,121,65,0.3)', borderRadius: '4px' }}>
                    <i className="ri-image-add-line text-2xl" style={{ color: 'rgba(200,121,65,0.55)' }}></i>
                  </div>
                  <p className="text-xs font-bold tracking-widest uppercase text-center" style={{ color: 'rgba(200,121,65,0.45)' }}>Imagen del incentivo</p>
                  <p className="text-white/20 text-[11px] text-center leading-relaxed">Espacio reservado para imagen oficial del programa</p>
                </div>

                {/* Aviso legal incentivo */}
                <div className="p-5" style={{ borderTop: '1px solid rgba(200,121,65,0.15)' }}>
                  <div className="flex items-start gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: 'rgba(255,107,26,0.15)', border: '1px solid rgba(255,107,26,0.3)', borderRadius: '3px' }}>
                      <i className="ri-information-line text-xs" style={{ color: '#FF6B1A' }}></i>
                    </div>
                    <p className="text-xs font-black uppercase tracking-widest" style={{ color: 'rgba(255,107,26,0.8)' }}>Aviso Legal — Beneficios del Programa</p>
                  </div>
                  <ul className="space-y-2">
                    {[
                      { icon: 'ri-car-line', text: 'Kit Profesional VELKO y Tarjeta Card Plus (8 asistencias nacionales): se entregan al completar tu RTM con el Taller Aliado. No dependen de la compra de llantas.' },
                      { icon: 'ri-bus-line', text: 'Servicio público de pasajeros: adicionalmente recibe Revisiones Preventivas Gratuitas al hacer su RTM.' },
                      { icon: 'ri-money-dollar-circle-line', text: 'Bono de Reembolso por Fidelidad: aplica únicamente si adquiriste llantas con la alianza. El monto depende del rin, diseño y valor de las llantas. La coordinación se realiza con el Taller Aliado y el operador.' },
                      { icon: 'ri-close-circle-line', text: 'Esta Actividad Promocional NO hace parte de rifas, juegos de azar ni espectáculos públicos. Es un beneficio del Programa Empresarial Privado.' },
                      { icon: 'ri-building-2-line', text: 'Programa impulsado por las marcas patrocinadoras y el aliado-taller u operador automotriz registrado en la Alianza de Beneficios.' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className={`${item.icon} text-xs flex-shrink-0 mt-0.5`} style={{ color: 'rgba(200,121,65,0.6)' }}></i>
                        <span className="text-white/35 text-[11px] leading-relaxed">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#020408', borderTop: '1px solid rgba(200,121,65,0.12)' }}>
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="py-10">
          <div className="max-w-6xl mx-auto px-6 flex items-center gap-5">
            <img src="https://static.readdy.ai/image/257ded5cf4b1165b373a01f4236f0b3a/e052755f997086e5f1ed7f5d0f41046d.png"
              alt="Tu Revisión Segura Logo" className="h-14 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))' }} />
            <div className="h-10 w-px bg-white/10"></div>
            <div>
              <p className="text-white font-black text-lg tracking-widest">REVISION SEGURA</p>
              <p className="text-white/30 text-xs tracking-widest">REVISION SEGURA</p>
            </div>
          </div>
        </div>
        <div className="py-14">
          <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 md:grid-cols-3 gap-10">
            <div>
              <h4 className="text-white/60 text-xs font-bold uppercase tracking-widest mb-4">Sobre el Programa</h4>
              <p className="text-white/25 text-xs leading-relaxed mb-5">Turevisionsegura.com es una plataforma de comunicación de Gestión Automotriz AW2020 SAS. Los beneficios son otorgados por la Alianza de Beneficios y son ajenos a la tarifa legal recaudada por el CDA. Estos premios y bonos no son descuentos directos en la tarifa de la revisión.</p>
              <div className="flex items-center gap-2 text-white/20 text-xs"><i className="ri-map-pin-line" style={{ color: 'rgba(200,121,65,0.5)' }}></i><span>Bogotá, Colombia</span></div>
              <div className="flex items-center gap-2 text-white/20 text-xs mt-1.5"><i className="ri-building-line" style={{ color: 'rgba(200,121,65,0.5)' }}></i><span>NIT 901401495</span></div>
            </div>
            <div>
              <h4 className="text-white/60 text-xs font-bold uppercase tracking-widest mb-4">Aliados</h4>
              <ul className="flex flex-col gap-2.5">
                {['VELKO', 'CARD PLUS', 'Gestión Automotriz AW2020', 'RTM-EC'].map((name) => (
                  <li key={name} className="flex items-center gap-2 text-white/25 text-sm"><i className="ri-checkbox-blank-circle-fill text-[6px]" style={{ color: 'rgba(200,121,65,0.4)' }}></i>{name}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }} className="py-5">
          <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/15 text-xs">&copy; 2025 REVISION SEGURA · Gestión Automotriz AW2020 SAS · Todos los derechos reservados.</p>
            <div className="flex items-center gap-4">
              <button onClick={() => window.open('https://lsddbkicdbwbilyhzcrf.supabase.co/storage/v1/object/public/DOCUMENTS/POLITICA-OFICIAL-PROGRAMA-CAV-O2.pdf', '_blank')}
                className="text-white/15 hover:text-white/40 text-xs transition-colors cursor-pointer whitespace-nowrap">Política Oficial</button>
              <span className="text-white/10">·</span>
              <a href="https://readdy.ai/?origin=logo" rel="nofollow" className="text-white/15 hover:text-white/40 text-xs transition-colors">Website Builder</a>
            </div>
          </div>
          <div className="max-w-6xl mx-auto px-6 mt-3">
            <p className="text-white/10 text-[10px] leading-relaxed text-center">
              Turevisionsegura.com es una plataforma de comunicación de Gestión Automotriz AW2020 SAS. Los beneficios son otorgados por la Alianza de Beneficios (Actividad Promocional) y son ajenos a la tarifa legal recaudada por el Centro de Diagnóstico (CDA). Estos premios y bonos no son descuentos directos en la tarifa de la revisión.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
